import React, { useCallback } from "react";
import Navbar from "../components/Navbar";
import BannerHeader from "../components/BannerHeader";
import { GetStaticProps, NextPage } from "next";
import {
  NewsArticleData,
  StrapiListResult,
  optimizedImageProps,
} from "../types";
import {
  getNewsBanner,
  getNewsArticleList,
  convertLocale,
  getPinnedNewsArticleList,
  shouldDisplayChineseLocale,
} from "../util/service";
import { ScrollToTopButton } from "../components/FloatingButton";
import { useRouter } from "next/router";
import {
  GetNextPageParamFunction,
  QueryFunction,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { NewsCard } from "../components/NewsCard";
import Head from "next/head";
import { NextSeo } from "next-seo";
import { getPlaiceholder } from "plaiceholder";

interface PageProps {
  banner: optimizedImageProps;
  pinnedArticles: NewsArticleData[];
}

export const getStaticProps: GetStaticProps<PageProps> = async ({ locale }) => {
  const _locale = shouldDisplayChineseLocale(locale) ? "zh" : locale;
  const loc = convertLocale(locale);
  const [banner, pinnedArticles] = await Promise.all([
    getNewsBanner(),
    getPinnedNewsArticleList(loc),
  ]);

  /* base64 image placeholder */

  // banner
  const bannerPlaiceholder = await getPlaiceholder(banner);
  const banner_optimized = {
    imageURL: bannerPlaiceholder.img.src,
    blurDataURL: bannerPlaiceholder.base64,
  };

  // pinned articles
  for (let i = 0; i < pinnedArticles.length; i++) {
    const { base64, img } = await getPlaiceholder(
      pinnedArticles[i].attributes.cover?.data.attributes.url ?? ""
    );
    pinnedArticles[i].attributes.blurDataURL = base64;
  }

  return {
    props: {
      banner: banner_optimized,
      pinnedArticles,
      messages: (await import(`../locales/${_locale}/common.json`)).default,
    },
    revalidate: 60,
  };
};

const News: NextPage<PageProps> = ({ banner, pinnedArticles }) => {
  const { locale } = useRouter();
  const loc = convertLocale(locale);

  const queryKey: string[] = ["news-list", loc];

  const queryFn: QueryFunction<
    StrapiListResult<NewsArticleData>,
    string[]
  > = useCallback(async ({ queryKey: [_, loc], pageParam }) => {
    return await getNewsArticleList(loc, 12, pageParam);
  }, []);

  const getNextPageParam: GetNextPageParamFunction<
    StrapiListResult<NewsArticleData>
  > = useCallback((_, page) => {
    if (page.length === page[0].meta.pagination.pageCount) {
      return undefined;
    } else {
      return page.length + 1;
    }
  }, []);

  const { data, fetchNextPage, isLoading, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey,
      queryFn,
      getNextPageParam,
    });

  const t = useTranslations("News");

  return (
    <>
      <NextSeo
        title="News | Animenz Piano Sheets"
        description="Find all of my news articles here!"
        openGraph={{
          images: [
            {
              url: banner.imageURL,
              width: 1200,
              height: 630,
              alt: "Animenz Piano Sheet",
            },
          ],
        }}
      />
      <Navbar />
      <BannerHeader
        img={banner}
        alt={t("altImage")}
        heading={t("title")}
        dark={false}
      />
      <div className="flex h-full w-screen flex-col place-items-center bg-timberwolf px-[20px] py-[50px] md:py-[60px]">
        <div className="flex w-full place-items-stretch justify-center">
          <div className="w-[70px] max-lg:hidden" />
          <div className="max-w-[350px] grow md:max-w-[730px] xl:max-w-[1110px]">
            {pinnedArticles.length > 0 && (
              <>
                <p className="mb-[30px] text-left font-libre-baskerville text-2xl font-semibold uppercase text-black sm:text-3xl">
                  {t("pinnedText")}
                </p>
                <div
                  className="
                    mb-[60px] grid
                    w-full grid-cols-1 place-content-center place-items-stretch
                    gap-[30px] md:grid-cols-2 xl:grid-cols-3
                  "
                >
                  {pinnedArticles.map((n) => (
                    <NewsCard key={n.id} news={n} pinned />
                  ))}
                </div>
              </>
            )}
            <p className="mb-[30px] text-left font-libre-baskerville text-2xl font-semibold uppercase text-black sm:text-3xl">
              {t("recentText")}
            </p>
            <div
              className="
                grid
                w-[100%] grid-cols-1 place-content-center place-items-stretch
                gap-[30px] md:grid-cols-2 xl:grid-cols-3
              "
            >
              {data?.pages.map((p) =>
                p.data?.map((n) => <NewsCard key={n.id} news={n} />)
              )}
            </div>
          </div>
          <div className="flex w-[70px] flex-col justify-end pl-[20px] max-lg:hidden">
            <div className="sticky bottom-[100px]">
              <ScrollToTopButton showBreakpoint={300} />
            </div>
          </div>
        </div>
        <div className="text-md mt-[30px] flex w-full justify-center text-center text-dim-gray">
          {isLoading || isFetchingNextPage ? (
            <div className="py-2 px-6">{t("loadingTexts.loadingMore")}</div>
          ) : hasNextPage ? (
            <button
              className="max-w-[350px] grow bg-cultured py-2 px-6 md:max-w-[730px] xl:max-w-[1110px]"
              onClick={() => fetchNextPage()}
            >
              {t("loadingTexts.loadMoreButtonText")}
            </button>
          ) : (
            <div className="py-2 px-6">
              {t("loadingTexts.noMoreScrollResults")}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default News;
